import { splatCacheKey } from './envCubemapCache.js'
import { environmentFileUrl } from './environmentFiles.js'
import { fetchWithRetry } from './fetchRetry.js'

/**
 * Navmesh artifact URLs for a splat (ENG-591/592). Two sources, tried in order:
 *
 *  1. SERVER (production) — the URL the asset-manager published for this
 *     environment's artifact. Included only when the server says the artifact
 *     exists, so a candidate list that comes back empty means "not baked"
 *     rather than "go and check".
 *  2. LOCAL KEYED (dev) — `public/assets/environments/navmesh/<splatCacheKey>.*`,
 *     committed for local testing without the bake service.
 *
 * `*Candidates()` return the URLs worth trying, in order; the runtime fetches
 * the first that exists (fetchFirstOk). Both are real baked artifacts — this is
 * the documented local-first → server transition, not a fallback to fake data.
 *
 * The server URL replaces a path this module used to compute from the splat's
 * path, which silently produced wrong paths once a second storage layout existed
 * (ENG-999). The dev path stays keyed by content hash and is ours, not the
 * server's, so it is still built here.
 */

async function localKeyedBase(splatUrl) {
  const key = await splatCacheKey(splatUrl)
  return `/assets/environments/navmesh/${key}`
}

/** [serverUrl?, localKeyedUrl] with the server entry dropped when not baked. */
async function candidates(splatUrl, name, localSuffix) {
  const served = await environmentFileUrl(splatUrl, name)
  const local = `${await localKeyedBase(splatUrl)}${localSuffix}`
  return served ? [served, local] : [local]
}

export async function navmeshCandidates(splatUrl) {
  return candidates(splatUrl, 'navmesh', '.json')
}

export async function collisionMeshCandidates(splatUrl) {
  return candidates(splatUrl, 'collision', '.collision.glb')
}

export async function voxelsCandidates(splatUrl) {
  return candidates(splatUrl, 'voxels', '.voxels.json')
}

/**
 * Fetch the first candidate URL that returns a real (non-HTML) 200. Returns
 * `{ url, res }` for the hit, or `{ url: <last>, res: <last response> }` so the
 * caller's own not-found handling (loud no-cache) still fires. `bust` is an
 * optional cache-busting query string (e.g. `?v=<version>`).
 */
export async function fetchFirstOk(urls, bust = '') {
  if (!urls || urls.length === 0) return { url: null, res: null }
  let last = { url: urls[urls.length - 1], res: null }
  for (const url of urls) {
    // Retry transient 5xx (e.g. asset-manager restarting under bake load) so a
    // momentary blip doesn't read as "artifact missing" (ENG-592).
    //
    // cache 'no-cache' = REVALIDATE, not "don't cache": the browser sends
    // If-None-Match and the server answers 304 (no body) when the artifact is
    // unchanged — measured on the org file route, which sends an ETag. Without
    // it these URLs sit behind `max-age=86400` busted only by ?v=splatVersion,
    // and splatVersion moves ONLY on a LiveTunePanel environment re-pick — so a
    // bake landing server-side (another user, a fixed bake service) stayed
    // invisible to every open session for up to a day, and the "fix" was asking
    // a non-technical user to re-pick their environment in a dropdown. A plain
    // page reload now always shows the current bake. splatVersion stays as the
    // proxy-level bust; bakeReload stays the same-session live signal.
    const res = await fetchWithRetry(url + bust, { cache: 'no-cache' })
    const ct = res.headers.get('content-type') || ''
    if (res.ok && !/html/.test(ct)) return { url, res }
    last = { url, res }
  }
  return last
}

// Back-compat single-URL helpers (local keyed) — retained for callers/tests not
// yet migrated to the candidate list.
export async function navmeshUrl(splatUrl) {
  return `${await localKeyedBase(splatUrl)}.json`
}
export async function collisionMeshUrl(splatUrl) {
  return `${await localKeyedBase(splatUrl)}.collision.glb`
}
export async function voxelsUrl(splatUrl) {
  return `${await localKeyedBase(splatUrl)}.voxels.json`
}
